import { useEffect, useMemo, useRef, useState } from "react";
import { navigate } from "../lib/router.ts";
import { Icon, type IconName } from "./icons.tsx";

export interface PaletteItem {
  group: string;
  label: string;
  icon: IconName;
  /** A keyboard hint or short detail on the right. */
  hint?: string;
  href?: string;
  run?: () => void;
}

/** Accent-insensitive, case-insensitive match. */
const fold = (s: string) => s.normalize("NFD").replace(/\p{Diacritic}/gu, "").toLowerCase();

/**
 * The command bar (⌘K / Ctrl+K): finds screens, collections and actions of the active store.
 * When nothing matches, the last row hands the request to the person's own AI (Conexões de IA).
 */
export function Palette({ items, open, onClose }: { items: PaletteItem[]; open: boolean; onClose: () => void }) {
  const [q, setQ] = useState("");
  const [idx, setIdx] = useState(0);
  const input = useRef<HTMLInputElement>(null);
  const list = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    setQ("");
    setIdx(0);
    requestAnimationFrame(() => input.current?.focus());
  }, [open]);

  const found = useMemo(() => {
    const t = fold(q.trim());
    const hit = items.filter((i) => !t || fold(i.label).includes(t) || fold(i.group).includes(t));
    const ai: PaletteItem = {
      group: "IA",
      label: q.trim() ? `Pedir à sua IA: ${q.trim()}` : "Pedir à sua IA para montar algo",
      icon: "ai",
      href: "/conexoes",
    };
    return [...hit, ai];
  }, [items, q]);

  useEffect(() => setIdx(0), [q]);
  useEffect(() => {
    list.current?.querySelector<HTMLElement>(`[data-i="${idx}"]`)?.scrollIntoView({ block: "nearest" });
  }, [idx]);

  if (!open) return null;

  const choose = (i: PaletteItem) => {
    onClose();
    if (i.run) i.run();
    else if (i.href) navigate(i.href);
  };

  let group = "";
  return (
    <div className="pal-scrim" onMouseDown={(e) => e.target === e.currentTarget && onClose()}>
      <div className="pal-box" role="dialog" aria-modal="true" aria-label="Barra de comando">
        <label className="pal-field">
          <Icon name="search" />
          <input
            ref={input}
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Buscar telas, dados e ações… ou pedir à IA"
            aria-label="Buscar"
            aria-controls="pal-list"
            aria-activedescendant={`pal-${idx}`}
            onKeyDown={(e) => {
              if (e.key === "ArrowDown") (e.preventDefault(), setIdx((idx + 1) % found.length));
              else if (e.key === "ArrowUp") (e.preventDefault(), setIdx((idx - 1 + found.length) % found.length));
              else if (e.key === "Enter") (e.preventDefault(), choose(found[idx]!));
              else if (e.key === "Escape") (e.preventDefault(), onClose());
            }}
          />
          <kbd>esc</kbd>
        </label>
        <div className="pal-list" id="pal-list" role="listbox" ref={list}>
          {found.map((item, i) => {
            const head = item.group !== group ? ((group = item.group), <div className="pal-grp">{item.group}</div>) : null;
            return (
              <div key={`${item.group}:${item.label}:${i}`}>
                {head}
                <div
                  id={`pal-${i}`}
                  data-i={i}
                  role="option"
                  aria-selected={i === idx}
                  className={`pal-it ${item.group === "IA" ? "ai" : ""}`}
                  onMouseMove={() => i !== idx && setIdx(i)}
                  onClick={() => choose(item)}
                >
                  <span className="dot">
                    <Icon name={item.icon} size={16} />
                  </span>
                  <span className="lbl">{item.label}</span>
                  {item.hint && <span className="hint-k">{item.hint}</span>}
                </div>
              </div>
            );
          })}
        </div>
        <div className="pal-foot">
          <span><kbd>↑↓</kbd>mover</span>
          <span><kbd>↵</kbd>abrir</span>
          <span><kbd>⌘K</kbd>abrir de qualquer tela</span>
        </div>
      </div>
    </div>
  );
}
